import { Link } from 'react-router-dom'
import { Zap, CheckCircle, FileText, Search, Shield, Rocket, Calendar, ArrowRight } from 'lucide-react'
import { SEOHead } from '../components/SEOHead'
import { useBlogPosts } from '../hooks/useBlog'
import { formatDate } from '../utils/helpers'

export function HomePage() {
  const { posts: latestPosts, loading: blogLoading } = useBlogPosts({ per_page: 4 })

  return (
    <>
      <SEOHead
        title="llm.txt Generator - Free AI SEO Tool & Resource Hub"
        description="Free llm.txt generator and AI SEO resource hub. Create properly formatted llm.txt files to help AI tools like ChatGPT, Claude, and Gemini better understand your website."
        keywords="llm.txt, AI SEO, ChatGPT SEO, Claude SEO, Gemini SEO, AI optimization, llm.txt generator, AI tools, website optimization"
        canonical="https://llmtext.net/"
        type="software"
      />
      <div className="bg-gradient-hero">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
            <div className="relative py-20 sm:py-28 lg:py-36">
              <div className="text-center">
                <h1 className="hero-title animate-fade-in">
                  Create llm.txt Files
                  <br />
                  <span className="text-gray-900">in Minutes</span>
                </h1>
                <p className="mx-auto mt-8 max-w-3xl text-xl leading-8 text-gray-700 animate-slide-up px-4">
                  Help AI tools like ChatGPT, Claude, and Gemini understand your website better. 
                  Our free generator helps you create properly formatted llm.txt files in seconds.
                </p>
                <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 animate-slide-up px-4">
                  <Link to="/generator" className="btn-primary w-full sm:w-auto text-center">
                    <Zap className="h-5 w-5 mr-2 inline" />
                    Generate Your llm.txt
                  </Link>
                  <Link to="/blog" className="text-sm font-semibold leading-6 text-gray-900 hover:text-primary-600 transition-colors w-full sm:w-auto text-center">
                    Learn More About AI SEO <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="text-center">
            <h2 className="text-base font-semibold leading-7 text-primary-600 uppercase tracking-wide">Why llm.txt?</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl px-4">
              Everything You Need for AI Optimization
            </p>
          </div>
          
          <div className="mt-20 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 px-4">
            <div className="card-gradient text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-primary">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <h3 className="mt-6 text-xl font-semibold leading-7 text-gray-900">Proper Formatting</h3>
              <p className="mt-4 text-base leading-7 text-gray-600">
                Ensure your llm.txt file follows the latest standards for AI consumption.
              </p>
            </div>

            <div className="card-gradient text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-secondary">
                <Search className="h-6 w-6 text-white" />
              </div>
              <h3 className="mt-6 text-xl font-semibold leading-7 text-gray-900">Improved Discovery</h3>
              <p className="mt-4 text-base leading-7 text-gray-600">
                Make it easier for AI agents to find and index your most important content.
              </p>
            </div>

            <div className="card-gradient text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-accent">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <h3 className="mt-6 text-xl font-semibold leading-7 text-gray-900">AI Control</h3>
              <p className="mt-4 text-base leading-7 text-gray-600">
                Take control of how AI models perceive and summarize your website.
              </p>
            </div>

            <div className="card-gradient text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-primary">
                <Rocket className="h-6 w-6 text-white" />
              </div>
              <h3 className="mt-6 text-xl font-semibold leading-7 text-gray-900">Fast Generation</h3>
              <p className="mt-4 text-base leading-7 text-gray-600">
                Create your file in seconds with our intuitive step-by-step generator.
              </p>
            </div>

            <div className="card-gradient text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-secondary">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
              <h3 className="mt-6 text-xl font-semibold leading-7 text-gray-900">Validation Built-in</h3>
              <p className="mt-4 text-base leading-7 text-gray-600">
                Our tool validates your input to ensure compatibility across all AI platforms.
              </p>
            </div>

            <div className="card-gradient text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-accent">
                <Calendar className="h-6 w-6 text-white" />
              </div>
              <h3 className="mt-6 text-xl font-semibold leading-7 text-gray-900">Always Current</h3>
              <p className="mt-4 text-base leading-7 text-gray-600">
                We keep our generator updated with the latest AI SEO best practices.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats/CTA Section */}
      <div className="bg-gradient-primary">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:px-8 sm:py-28 lg:px-12">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl px-4">
              Don't Let AI Miss Your Best Content
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-primary-100 px-4">
              As AI becomes the primary way people find information online, having an llm.txt file is no longer optional—it's essential for modern SEO.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 px-4">
              <Link to="/generator" className="bg-white text-primary-600 hover:bg-gray-50 font-semibold py-3 px-12 rounded-lg transition-colors whitespace-nowrap w-full sm:w-auto text-center">
                Get Started Now
              </Link>
              <Link to="/resources" className="text-white hover:text-primary-100 font-semibold w-full sm:w-auto text-center">
                Explore Resources <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Blog/Resources Preview */}
      <div className="bg-gray-50 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl mb-4">Latest from Our AI SEO Blog</h2>
            <p className="text-lg text-gray-600">Tips, tricks, and strategies to improve your AI visibility.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 px-4">
            {!blogLoading && latestPosts.map((post) => (
              <div key={post.slug} className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-gray-100">
                <Link to={`/blog/${post.slug}`} className="block h-full">
                  {post.hero_image && (
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={post.hero_image} 
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="mb-3">
                      <span className="inline-flex items-center rounded-md bg-primary-50 px-2 py-1 text-xs font-medium text-primary-700 ring-1 ring-inset ring-primary-700/10">
                        {post.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary-600 transition-colors mb-2 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-auto">
                      <div className="flex items-center text-xs text-gray-500">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDate(post.created_at)}
                      </div>
                      <span className="text-primary-600 text-xs font-semibold group-hover:translate-x-1 transition-transform inline-block">Read More →</span>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/blog" className="text-primary-600 font-semibold hover:text-primary-700 transition-colors">
              View All Articles <ArrowRight className="h-4 w-4 inline ml-1" />
            </Link>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-primary-600 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl px-4">
              Ready to Optimize Your Website for AI?
            </h2>
            <p className="mt-8 text-xl leading-8 text-primary-100 max-w-3xl mx-auto px-4">
              Join thousands of websites already using llm.txt to improve their AI discoverability. Create your file in minutes and start seeing results immediately.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 px-4">
              <Link to="/generator" className="bg-white text-primary-600 hover:bg-gray-50 font-semibold py-3 px-12 rounded-lg transition-colors whitespace-nowrap w-full sm:w-auto text-center">
                <Zap className="h-5 w-5 mr-2 inline" />
                Create Your llm.txt
              </Link>
              <Link to="/blog" className="text-white hover:text-primary-100 font-semibold w-full sm:w-auto text-center">
                Read Our AI SEO Guide →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
